import React, { useState, useEffect } from 'react';
import { ExpenseEntry } from '../types';

interface TagManagerProps {
  expense: ExpenseEntry;
  onUpdateTags: (tags: string[]) => void;
}

const TagManager: React.FC<TagManagerProps> = ({ expense, onUpdateTags }) => {
  const [tags, setTags] = useState<string[]>(expense.tags || []);
  const [newTag, setNewTag] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [baseSuggestedTags, setBaseSuggestedTags] = useState<string[]>([
    'Business', 
    'Personal', 
    'Reimbursable', 
    'Client Meeting',
    'Recurring',
    'One-time',
    'Receipt Available',
    'Needs Review',
    'Important'
  ]);

  // Generate category-specific tag suggestions
  useEffect(() => {
    const categorySuggestions = getCategorySuggestions(expense.category);
    const expenseCOGSSuggestions = getExpenseCOGSSuggestions(expense.expenseCOGS);
    const combinedSuggestions = [...baseSuggestedTags, ...categorySuggestions, ...expenseCOGSSuggestions];
    
    // Filter out any duplicates
    const uniqueSuggestions = Array.from(new Set(combinedSuggestions));
    setFilteredSuggestions(uniqueSuggestions);
  }, [expense.category, expense.expenseCOGS, baseSuggestedTags]);

  // Updates tags when expense changes (for example, when switching between expenses)
  useEffect(() => {
    setTags(expense.tags || []);
  }, [expense.tags]);

  const getCategorySuggestions = (category: string): string[] => {
    // Return specific tag suggestions based on expense category
    switch(category) {
      case 'Advertising':
        return ['Digital Ads', 'Print', 'Social Media', 'Campaign'];
      case 'Meals and Entertainment':
        return ['Client Meal', 'Team Lunch', 'Business Dinner'];
      case 'Office Expenses':
        return ['Supplies', 'Furniture', 'Equipment'];
      case 'Professional Services':
        return ['Legal', 'Accounting', 'Consulting'];
      case 'SaaS and Software':
        return ['Monthly Subscription', 'Annual License', 'Cloud Service'];
      case 'Travel':
        return ['Airfare', 'Hotel', 'Taxi', 'Car Rental', 'Per Diem'];
      case 'Utilities':
        return ['Electricity', 'Water', 'Internet', 'Phone'];
      default:
        return [];
    }
  };

  const getExpenseCOGSSuggestions = (expenseCOGS: string): string[] => {
    // Return specific tag suggestions based on expense/COGS type
    switch(expenseCOGS) {
      case 'Expense':
        return ['Operational', 'Administrative', 'Non-billable'];
      case 'COGS':
        return ['Direct Cost', 'Project Related', 'Billable', 'Client Reimbursable'];
      default:
        return [];
    }
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    
    const trimmedTag = newTag.trim();
    if (!tags.includes(trimmedTag)) {
      const updatedTags = [...tags, trimmedTag];
      setTags(updatedTags);
      onUpdateTags(updatedTags);
      
      // Add to suggested tags if not already there
      if (!baseSuggestedTags.includes(trimmedTag)) {
        setBaseSuggestedTags([...baseSuggestedTags, trimmedTag]);
      }
    }
    
    setNewTag('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove);
    setTags(updatedTags);
    onUpdateTags(updatedTags);
  };

  const handleSuggestedTagClick = (tag: string) => {
    if (!tags.includes(tag)) {
      const updatedTags = [...tags, tag];
      setTags(updatedTags);
      onUpdateTags(updatedTags);
    }
  };

  // Quick buttons to add common tags based on category and type
  const handleAddQuickTags = (tagType: 'category' | 'expenseCOGS') => {
    let tagsToAdd: string[] = [];
    
    if (tagType === 'category') {
      tagsToAdd = getCategorySuggestions(expense.category);
    } else {
      tagsToAdd = getExpenseCOGSSuggestions(expense.expenseCOGS);
    }
    
    // Only add tags that aren't already in the list
    const newTags = tagsToAdd.filter(tag => !tags.includes(tag));
    
    if (newTags.length > 0) {
      const updatedTags = [...tags, ...newTags];
      setTags(updatedTags);
      onUpdateTags(updatedTags);
    }
  };

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-2 mb-2 min-h-[30px]">
        {tags.map(tag => (
          <span 
            key={tag} 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              className="ml-1 -mr-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none"
            >
              <span className="sr-only">Remove tag</span>
              ✕
            </button>
          </span>
        ))}
        {tags.length === 0 && (
          <span className="text-xs text-gray-500">No tags added</span>
        )}
      </div>
      
      <div className="flex">
        <div className="relative flex-1">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a tag..."
            className="block w-full pl-2 pr-12 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="absolute inset-y-0 right-0 px-2 py-1 text-xs text-blue-600 hover:text-blue-800"
          >
            Add
          </button>
        </div>
      </div>
      
      {/* Quick buttons for adding category or expense type related tags */}
      <div className="mt-2 flex space-x-2">
        <button
          type="button"
          onClick={() => handleAddQuickTags('category')}
          className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          + {expense.category} Tags
        </button>
        <button
          type="button"
          onClick={() => handleAddQuickTags('expenseCOGS')}
          className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          + {expense.expenseCOGS} Tags
        </button>
      </div>
      
      {filteredSuggestions.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-1">Suggested tags:</p>
          <div className="flex flex-wrap gap-1">
            {filteredSuggestions
              .filter(tag => !tags.includes(tag))
              .slice(0, 8)
              .map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleSuggestedTagClick(tag)}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
                >
                  {tag}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagManager; 